import { Component, OnInit, Input } from '@angular/core';
import type { EChartsOption } from 'echarts';
import { catchError, debounceTime, Subscription, throwError } from 'rxjs';
import { GibbPPMData } from 'src/app/models/gibb-ppm-data';
import { SyncService } from 'src/app/services/sync-service';
import { FieldDashboardDataService } from '../field-dashboard-data.service';
import { formatDate } from '@angular/common';

@Component({
  selector: 'app-gibberellin',
  templateUrl: './gibberellin.component.html',
  styleUrls: ['./gibberellin.component.css'],
  standalone: false
})
export class GibberellinComponent implements OnInit {
  @Input() fieldId: string = '';
  @Input() farmId: string;

  @Input() fromDate: String;
  @Input() toDate: String;

  chartOption: EChartsOption = {};
  hasData: boolean = true;
  isLoading: boolean = true;

  gibbPPM: GibbPPMData = {
    cultivars: [],
    legends: [],
    weeks: [],
    data: [[]]
  };

  private subscription: Subscription;

  public series: any[] = [];

  constructor(
    private syncService: SyncService,
    private dataService: FieldDashboardDataService
  ) {
  }


  ngOnInit(): void {
    this.subscription = this.dataService.fieldData$.pipe(debounceTime(100)).subscribe(data => {
      if (data.fieldId && data.farmId && data.startDate && data.endDate) {
        let fromDate: string;
        let toDate: string;

        if (data.userChangedDates) {
          fromDate = formatDate(data.startDate, 'yyyy-MM-dd', 'en-ZA');
          toDate = formatDate(data.endDate, 'yyyy-MM-dd', 'en-ZA');
        } else if (data.season) {
          const startYear = parseInt(data.season.split('/')[0]);
          fromDate = `${startYear}-06-01`;
          toDate = `${startYear + 1}-05-31`;
        } else {
          return;
        }

        if (this.fieldId === data.fieldId && this.fromDate === fromDate && this.toDate === toDate) return;

        this.fieldId = data.fieldId;
        this.farmId = '2475';
        this.fromDate = fromDate;
        this.toDate = toDate;
        this.populateData();
      }
    });
  }

  color = ['#0d47a1', '#1565c0', '#1976d2', '#1e88e5', '#42a5f5', '#90caf9', '#bbdefb'];

  populateData() {
    this.isLoading = true;
    this.hasData = true;
    console.log("From date", this.fromDate);
    console.log("To date", this.toDate);
    this.syncService.getGibbPPM(this.fieldId, this.fromDate, this.toDate).pipe(
      catchError(error => {
        console.error();
        return throwError(() => error);
      })
    ).subscribe((response) => {
      this.gibbPPM = response;
      this.isLoading = false;
      this.loadChartData();
    })
  }

  loadChartData(): void {
    this.hasData = this.gibbPPM.cultivars.length > 0 && this.gibbPPM.data[0]?.some(v => v !== 0);
    if (!this.hasData) return;
    this.series = [];

    // Group by week - assign same color to same week
    const weekColorMap = new Map<number, string>();
    let colorIdx = 0;
    for (const week of this.gibbPPM.weeks) {
      if (!weekColorMap.has(week)) {
        weekColorMap.set(week, this.color[colorIdx % this.color.length]);
        colorIdx++;
      }
    }

    for (let i = 0; i < this.gibbPPM.legends.length; i++) {
      if (this.gibbPPM.data[0][i] === 0) continue;
      const week = this.gibbPPM.weeks[i];
      this.series.push({
        name: 'Week ' + week,
        type: 'bar',
        stack: 'gibb',
        data: [this.gibbPPM.data[0][i]],
        label: {
          show: true,
          position: 'inside',
          formatter: () => this.gibbPPM.legends[i] + ' (' + this.gibbPPM.data[0][i] + ')'
        },
        itemStyle: { color: weekColorMap.get(week) }
      });
    }

    // Unique week legends
    const uniqueWeeks = [...new Set(this.gibbPPM.weeks.filter((_, i) => this.gibbPPM.data[0][i] !== 0))];

    this.chartOption = {
      title: {
        text: 'Gibb PPM',
        left: 0,
        top: 10,
        textStyle: {
          fontSize: 18,
          fontWeight: 'bold'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          let result = '';
          params.filter(p => p.value !== 0).forEach(p => {
            result += `${p.marker} ${this.gibbPPM.legends[p.seriesIndex]} (${p.seriesName}): ${p.value}<br/>`;
          });
          return result;
        }
      },
      legend: {
        data: uniqueWeeks.map(w => 'Week ' + w),
        bottom: 0,
        itemGap: 25,
        type: 'scroll'
      },
      grid: {
        left: '10%',
        right: '5%',
        bottom: '10%',
        top: '25%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: this.gibbPPM.cultivars
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: '{value}'
        }
      },
      series: this.series
    };
  }
}
